/**
 * Utility helpers to convert between CRM phone numbers and WhatsApp JIDs.
 * Kept framework-agnostic so both the Next.js API routes and the Vite client
 * can share the exact same normalization logic.
 */

/**
 * Convert a phone number (ideally E.164 like +919999999999) into a WhatsApp JID.
 * Returns null if the input cannot be converted into a numeric WhatsApp user id.
 */
const PERSONAL_JID_DOMAINS = ['s.whatsapp.net', 'c.us', 'l.whatsapp.net'] as const;

export function phoneNumberToWhatsAppJid(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (!digits) {
    return null;
  }
  return `${digits}@s.whatsapp.net`;
}

/**
 * Convert a WhatsApp JID back into the normalized phone representation.
 * Returns null for LID/group/invalid JIDs.
 */
export function whatsappJidToPhone(
  jid: string | null | undefined,
): string | null {
  if (!jid) return null;
  const trimmed = jid.trim().toLowerCase();
  const [user, domain] = trimmed.split('@');
  if (!user || !domain || !PERSONAL_JID_DOMAINS.includes(domain as typeof PERSONAL_JID_DOMAINS[number])) {
    return null;
  }
  const phoneDigits = user.split(':')[0];
  if (!/^\d+$/.test(phoneDigits)) {
    return null;
  }
  return `+${phoneDigits}`;
}

export function candidateJidsForPhone(
  phone: string | null | undefined,
  stored?: string | null | undefined,
): string[] {
  const candidates: Array<string | null> = [
    stored?.trim().toLowerCase() ?? null,
    phoneNumberToWhatsAppJid(phone),
  ];

  if (phone) {
    const digits = phone.replace(/\D/g, '');
    if (digits) {
      candidates.push(`${digits}@c.us`, `${digits}@l.whatsapp.net`);
    }
  }

  return candidates.reduce<string[]>((list, jid) => {
    if (jid && !list.includes(jid)) {
      list.push(jid);
    }
    return list;
  }, []);
}
